import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

// Preferência de recolhido/expandido por seção (persiste entre visitas)
const COLLAPSED_STORE_KEY = 'dp_skills_sections_collapsed';

function loadCollapsed(storageKey) {
  if (!storageKey) return false;
  try {
    const map = JSON.parse(localStorage.getItem(COLLAPSED_STORE_KEY) || '{}');
    return !!map[storageKey];
  } catch {
    return false;
  }
}

function persistCollapsed(storageKey, collapsed) {
  if (!storageKey) return;
  try {
    const map = JSON.parse(localStorage.getItem(COLLAPSED_STORE_KEY) || '{}');
    if (collapsed) map[storageKey] = true;
    else delete map[storageKey];
    localStorage.setItem(COLLAPSED_STORE_KEY, JSON.stringify(map));
  } catch { /* noop */ }
}

/**
 * Painel de seção no padrão do Text to Speech (Gerador de Áudio):
 * card com barra de gradiente no topo (dp-top-accent), header interno
 * com título + badge de contagem em gradiente + ações, e conteúdo com
 * padding próprio.
 *
 * `collapsible`: clicar no header recolhe/expande o conteúdo (chevron
 * indica o estado). Com `storageKey`, a preferência persiste no
 * localStorage por seção. Cliques nas `actions` NÃO alternam o painel.
 */
const SectionPanel = ({ icon: Icon, title, count, actions, children, style, collapsible = false, storageKey = null }) => {
  const [collapsed, setCollapsed] = useState(() => collapsible && loadCollapsed(storageKey));

  const toggle = () => {
    if (!collapsible) return;
    setCollapsed((prev) => {
      persistCollapsed(storageKey, !prev);
      return !prev;
    });
  };

  return (
    <section className="dp-panel dp-top-accent" style={{ marginBottom: '20px', ...style }}>
      {/* Header do painel (clicável quando collapsible) */}
      <div
        onClick={toggle}
        role={collapsible ? 'button' : undefined}
        aria-expanded={collapsible ? !collapsed : undefined}
        style={{
          display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap',
          padding: '13px 16px 11px',
          borderBottom: collapsed ? 'none' : '1px solid var(--dp-border-subtle)',
          cursor: collapsible ? 'pointer' : 'default',
          userSelect: collapsible ? 'none' : 'auto',
        }}
      >
        {Icon && <Icon size={15} style={{ color: 'var(--dp-blue-500, #0DAEFF)', flexShrink: 0 }} />}
        <span style={{
          fontSize: '13px', fontWeight: 600, color: 'var(--dp-text-primary)',
          fontFamily: 'var(--dp-font)', letterSpacing: '0.2px',
          minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {title}
        </span>
        {count != null && (
          <span style={{
            background: 'linear-gradient(135deg, #0DAEFF 0%, #3B82F6 100%)',
            color: '#FFFFFF', fontSize: '10px', fontWeight: 700,
            padding: '2px 8px', borderRadius: '9999px',
            fontFamily: 'var(--dp-font)', flexShrink: 0,
          }}>
            {count}
          </span>
        )}
        <span style={{ flex: 1 }} />
        {/* Ações não alternam o painel */}
        {actions && (
          <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
            {actions}
          </span>
        )}
        {collapsible && (
          <ChevronDown
            size={15}
            style={{
              color: 'var(--dp-text-tertiary)', flexShrink: 0,
              transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)',
              transition: 'transform 0.18s ease',
            }}
          />
        )}
      </div>
      {/* Conteúdo (desmontado quando recolhido — seções grandes ficam leves) */}
      {!collapsed && (
        <div style={{ padding: '16px' }}>
          {children}
        </div>
      )}
    </section>
  );
};

export default SectionPanel;
